/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Visible label; linked to the control for accessibility. */
  label: ReactNode;
  /** When set, shows error text and invalid semantics. */
  error?: ReactNode;
}

/**
 * Labelled text input with optional error region (TrueRead-compliant spacing and focus ring via CSS tokens).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, id: idProp, className, 'aria-describedby': ariaDescribedBy, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = idProp ?? reactId;
  const errorId = `${inputId}-error`;
  const hasError = error !== undefined && error !== null && error !== '';

  const rootClass = ['ecs-input-field', hasError ? 'ecs-input-field--error' : '', className]
    .filter(Boolean)
    .join(' ');

  const describedBy =
    [hasError ? errorId : '', ariaDescribedBy].filter(Boolean).join(' ') || undefined;

  return (
    <div className={rootClass}>
      <label className="ecs-input-field__label" htmlFor={inputId}>
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        className="ecs-input-field__input"
        aria-invalid={hasError ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      {hasError ? (
        <div id={errorId} className="ecs-input-field__error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
});
